import { Badge } from '@/components/shared/Badge';
import { Panel } from '@/components/shared/Panel';
import { RoleEmblem } from '@/components/shared/RoleEmblem';
import { getPlayerAttendanceStat } from '@/features/attendance/selectors';
import { getPlayerProgression } from '@/features/progression/logic';
import { useGuildData } from '@/hooks/useGuildData';
import type { Profile } from '@/types/domain';
import { getProfileInGameName } from '@/utils/profile';
import { getPrimaryRole } from '@/utils/roles';

interface ProfileSettingsPanelProps {
  profile: Profile | null;
}

export function ProfileSettingsPanel({ profile }: ProfileSettingsPanelProps): JSX.Element {
  const { state } = useGuildData();
  const primaryRole = profile ? getPrimaryRole(profile.roles) : 'member';
  const attendanceCount = profile ? getPlayerAttendanceStat(state, profile.id)?.totalCountedMasses ?? 0 : 0;
  const progression = profile
    ? getPlayerProgression(profile, attendanceCount)
    : {
        level: 1,
        tier: 'Recruit',
      };

  return (
    <Panel
      title="Profile Overview"
      description="Discord identity remains auth-derived. In-Game Name is set during onboarding, then managed only by guild staff so mass rosters stay consistent."
    >
      <div className="form-grid form-grid--two">
        <label className="field">
          <span className="field__label">Discord Display Name</span>
          <input className="field__input" value={profile?.displayName ?? ''} disabled />
        </label>
        <label className="field">
          <span className="field__label">Discord Username</span>
          <input className="field__input" value={profile?.discordUsername ? `@${profile.discordUsername}` : ''} disabled />
        </label>
      </div>

      <div className="form-grid form-grid--two">
        <label className="field">
          <span className="field__label">In-Game Name</span>
          <input className="field__input" value={profile?.inGameName ?? ''} disabled />
        </label>
        <label className="field">
          <span className="field__label">Roster Display</span>
          <input className="field__input" value={getProfileInGameName(profile)} disabled />
        </label>
      </div>

      <div className="profile-summary">
        <div>
          <span className="profile-summary__label">Approval</span>
          <strong>{profile?.approvalStatus ?? 'pending'}</strong>
          <span className="profile-summary__hint">Only approved members can access live CTA signups.</span>
        </div>
        <div>
          <span className="profile-summary__label">Guild Identity</span>
          {profile ? <RoleEmblem role={primaryRole} /> : <strong>Member</strong>}
          <span className="profile-summary__hint">Your visible guild rank appears across chat, roster rows, and player stats.</span>
        </div>
        <div>
          <span className="profile-summary__label">Progression</span>
          <strong>Level {progression.level}</strong>
          <span className="profile-summary__hint">{progression.tier}</span>
        </div>
        <div>
          <span className="profile-summary__label">Badges</span>
          <div className="slot-card__badges">
            {profile && profile.badges.length > 0 ? (
              profile.badges.slice(0, 3).map((badge) => (
                <Badge key={badge.badgeId} tone="warning">
                  {badge.displayName}
                </Badge>
              ))
            ) : (
              <Badge tone="default">No badges yet</Badge>
            )}
          </div>
        </div>
        <div>
          <span className="profile-summary__label">How to change names</span>
          <strong>Ask an officer or admin</strong>
          <span className="profile-summary__hint">Guild staff now manage in-game names from the user-management dashboard.</span>
        </div>
      </div>
    </Panel>
  );
}
